import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LazyVideo, Reveal, Section } from "./shared";

const Card = ({ title, children, className = "", delay = 0, testId }) => (
    <Reveal delay={delay} className={className}>
        <div
            data-testid={testId}
            className="group flex h-full flex-col rounded-[2rem] bg-white p-6 transition-transform duration-300 hover:-translate-y-1"
        >
            <p className="font-display text-base font-bold tracking-[-0.02em] text-ink">{title}</p>
            <div className="mt-5 min-h-[90px] flex-1 overflow-hidden rounded-2xl bg-paper">{children}</div>
        </div>
    </Reveal>
);

const Stickman = ({ className = "h-14 w-9" }) => (
    <svg viewBox="0 0 36 60" fill="none" className={className} aria-hidden="true">
        <circle cx="18" cy="8" r="6" stroke="#0B0812" strokeWidth="3" />
        <line x1="18" y1="15" x2="18" y2="36" stroke="#0B0812" strokeWidth="3" strokeLinecap="round" />
        <motion.line x1="18" y1="20" x2="9" y2="30" stroke="#0B0812" strokeWidth="3" strokeLinecap="round"
            animate={{ x2: [9, 27, 9] }} transition={{ duration: 0.55, repeat: Infinity, ease: "easeInOut" }} />
        <motion.line x1="18" y1="20" x2="27" y2="30" stroke="#0B0812" strokeWidth="3" strokeLinecap="round"
            animate={{ x2: [27, 9, 27] }} transition={{ duration: 0.55, repeat: Infinity, ease: "easeInOut" }} />
        <motion.line x1="18" y1="36" x2="10" y2="56" stroke="#0B0812" strokeWidth="3" strokeLinecap="round"
            animate={{ x2: [10, 26, 10] }} transition={{ duration: 0.55, repeat: Infinity, ease: "easeInOut" }} />
        <motion.line x1="18" y1="36" x2="26" y2="56" stroke="#0B0812" strokeWidth="3" strokeLinecap="round"
            animate={{ x2: [26, 10, 26] }} transition={{ duration: 0.55, repeat: Infinity, ease: "easeInOut" }} />
    </svg>
);

const GoogleG = ({ className = "h-4 w-4" }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
        <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
    </svg>
);

const InstagramIcon = ({ className = "h-4 w-4" }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <defs>
            <linearGradient id="ig-grad" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0%" stopColor="#feda75" />
                <stop offset="45%" stopColor="#d62976" />
                <stop offset="100%" stopColor="#4f5bd5" />
            </linearGradient>
        </defs>
        <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig-grad)" />
        <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8" fill="none" />
        <circle cx="17.2" cy="6.8" r="1.3" fill="white" />
    </svg>
);

const QrCode = ({ className = "h-11 w-11" }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <rect x="1" y="1" width="22" height="22" rx="2" fill="white" stroke="#0B0812" strokeWidth="1.5" />
        {[[3, 3], [15, 3], [3, 15]].map(([x, y]) => (
            <g key={`${x}-${y}`}>
                <rect x={x} y={y} width="6" height="6" fill="#0B0812" />
                <rect x={x + 1} y={y + 1} width="4" height="4" fill="white" />
                <rect x={x + 2} y={y + 2} width="2" height="2" fill="#0B0812" />
            </g>
        ))}
        {[[12, 4], [13, 7], [11, 11], [14, 12], [17, 11], [12, 15], [15, 15], [18, 18], [12, 19], [16, 20], [19, 14]].map(([x, y]) => (
            <rect key={`${x}-${y}`} x={x} y={y} width="2" height="2" fill="#7A60E2" />
        ))}
    </svg>
);

const Storefront = ({ className = "h-14 w-14", open = false }) => (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
        <path d="M4 16 L9 6 h30 l5 10 z" fill="#7A60E2" stroke="#0B0812" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="6" y="16" width="36" height="26" rx="2" fill="white" stroke="#0B0812" strokeWidth="2.5" />
        <rect x="11" y="23" width="10" height="9" rx="1" fill="#EDE9FE" stroke="#0B0812" strokeWidth="2" />
        <rect x="27" y="23" width="10" height="19" rx="1" fill={open ? "#7A60E2" : "#DAD7D0"} stroke="#0B0812" strokeWidth="2" />
    </svg>
);

const JOURNEY_STEPS = [
    "Discovers your business",
    "Searches on Google",
    "You rank #1 on Google",
    "Sees your Instagram ad",
    "Walks into your store",
    "Scans Purple QR · rates 5★",
];

const JourneyProp = ({ step }) => {
    switch (step) {
        case 0:
            return <Storefront className="h-14 w-14" />;
        case 1:
            return (
                <div className="flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white py-1.5 pl-2 pr-3 shadow-sm">
                    <GoogleG className="h-4 w-4" />
                    <span className="whitespace-nowrap text-[10px] text-neutral-600">
                        best salon near me
                        <motion.span
                            animate={{ opacity: [1, 0, 1] }}
                            transition={{ duration: 0.9, repeat: Infinity }}
                            className="ml-px inline-block h-2.5 w-px translate-y-0.5 bg-neutral-500"
                        />
                    </span>
                </div>
            );
        case 2:
            return (
                <div className="w-36 rounded-lg border border-neutral-200 bg-white p-2 shadow-sm">
                    <div className="flex items-center gap-1">
                        <GoogleG className="h-3 w-3" />
                        <span className="text-[8px] text-neutral-500">yourbusiness.in</span>
                        <span className="ml-auto rounded bg-[#7A60E2] px-1 py-px text-[7px] font-bold text-white">#1</span>
                    </div>
                    <p className="mt-1 text-[10px] font-bold leading-tight text-[#1a0dab]">Your Business — Top Rated Near You</p>
                    <p className="mt-0.5 text-[8px] text-[#FBBC04]">★★★★★ <span className="text-neutral-400">4.9 · 210 reviews</span></p>
                </div>
            );
        case 3:
            return (
                <div className="w-28 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
                    <div className="flex items-center gap-1 px-1.5 py-1">
                        <InstagramIcon className="h-3 w-3" />
                        <span className="text-[8px] font-bold text-ink">your.business</span>
                        <span className="ml-auto text-[7px] text-neutral-400">Sponsored</span>
                    </div>
                    <div className="flex h-10 items-center justify-center bg-gradient-to-br from-[#7A60E2] to-[#d62976]">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/90">
                            <svg viewBox="0 0 24 24" className="ml-px h-2.5 w-2.5 fill-ink"><path d="M8 5v14l11-7z" /></svg>
                        </span>
                    </div>
                </div>
            );
        case 4:
            return (
                <div className="flex items-center gap-1.5">
                    <Storefront className="h-14 w-14" open />
                    <span className="rounded-full bg-ink px-2 py-0.5 text-[8px] font-bold text-white">OPEN</span>
                </div>
            );
        default:
            return (
                <div className="flex items-center gap-2">
                    <QrCode className="h-11 w-11" />
                    <div>
                        <p className="text-xs leading-none tracking-tight text-[#FBBC04]">★★★★★</p>
                        <p className="mt-1 text-[8px] font-bold text-neutral-500">5-star review posted</p>
                    </div>
                </div>
            );
    }
};

const JourneyMock = () => {
    const [step, setStep] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setStep((s) => (s + 1) % JOURNEY_STEPS.length), 2100);
        return () => clearInterval(t);
    }, []);
    return (
        <div data-testid="journey-animation" className="flex h-full min-h-[132px] flex-col p-3">
            <div className="flex flex-1 items-center justify-between gap-2 overflow-hidden px-1">
                <motion.div
                    animate={{ y: [0, -2.5, 0] }}
                    transition={{ duration: 0.55, repeat: Infinity, ease: "easeInOut" }}
                    className="shrink-0"
                >
                    <Stickman />
                </motion.div>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 14, scale: 0.9 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: -14, scale: 0.9 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="flex min-w-0 justify-end"
                    >
                        <JourneyProp step={step} />
                    </motion.div>
                </AnimatePresence>
            </div>
            <div className="mt-1 h-0.5 w-full rounded bg-neutral-200" />
            <div className="mt-1.5 flex items-center justify-between gap-2">
                <AnimatePresence mode="wait">
                    <motion.p
                        key={step}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.25 }}
                        className="text-[10px] font-bold text-ink"
                    >
                        {JOURNEY_STEPS[step]}
                    </motion.p>
                </AnimatePresence>
                <div className="flex shrink-0 gap-1">
                    {JOURNEY_STEPS.map((_, i) => (
                        <span key={i} className={`h-1 w-1 rounded-full transition-colors duration-300 ${i === step ? "bg-ink" : "bg-neutral-300"}`} />
                    ))}
                </div>
            </div>
        </div>
    );
};

const Keyword = ({ children }) => (
    <span className="rounded bg-[#7A60E2]/15 px-0.5 font-semibold text-[#7A60E2]">{children}</span>
);

const ReviewReplyMock = () => (
    <div data-testid="review-reply-mock" className="flex h-full flex-col justify-center gap-2 p-3">
        <div className="rounded-xl border border-neutral-200 bg-white p-3 text-left shadow-sm">
            <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#7A60E2] text-[10px] font-bold text-white">P</span>
                <div className="min-w-0 leading-tight">
                    <p className="truncate text-[11px] font-bold text-ink">Priya Sharma</p>
                    <p className="text-[9px] text-neutral-400">Local Guide · 2 weeks ago</p>
                </div>
                <GoogleG className="ml-auto h-3.5 w-3.5 shrink-0 opacity-80" />
            </div>
            <p className="mt-1.5 text-[11px] leading-none tracking-tight text-[#FBBC04]">★★★★★</p>
            <p className="mt-1 text-[11px] leading-snug text-neutral-700">“Loved the haircut and the ambience. Staff was so friendly!”</p>
            <div className="mt-2 rounded-lg bg-[#F0F4F9] p-2">
                <p className="text-[9px] font-semibold text-neutral-500">
                    Response from the owner <span className="font-normal text-neutral-400">· just now · written by Purple AI</span>
                </p>
                <p className="mt-1 text-[10px] leading-snug text-neutral-700">
                    Thank you, Priya! Thrilled you loved the <Keyword>best salon in Indiranagar</Keyword>. Our <Keyword>hair spa</Keyword> and <Keyword>bridal makeup</Keyword> guests say the same — see you soon!
                </p>
            </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2 py-1.5 shadow-sm">
            <GoogleG className="h-3 w-3 shrink-0" />
            <p className="text-[9px] text-neutral-500">
                Keyword-rich replies help you rank <span className="rounded bg-ink px-1 py-px text-[8px] font-bold text-white">#1</span> for “<Keyword>best salon in Indiranagar</Keyword>”
            </p>
        </div>
    </div>
);

const SponsoredLinkAd = () => (
    <div className="h-full w-full rounded-lg border border-neutral-200 bg-white p-2.5 text-left shadow-md">
        <p className="text-[8px] font-bold text-ink">Sponsored</p>
        <div className="mt-0.5 flex items-center gap-1">
            <span className="flex h-3 w-3 items-center justify-center rounded-full bg-[#7A60E2] text-[6px] font-bold text-white">Y</span>
            <span className="text-[8px] text-neutral-600">yourbusiness.in</span>
        </div>
        <p className="mt-0.5 text-[10px] font-bold leading-tight text-[#1a0dab]">Best Salon in Indiranagar | Book Online</p>
        <p className="mt-0.5 text-[8px] leading-snug text-neutral-500">4.9★ rated · Open today · Easy online booking, festive offers & more.</p>
    </div>
);

const InstagramVideoAd = () => (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white text-left shadow-md">
        <div className="flex items-center gap-1 px-1.5 py-1">
            <InstagramIcon className="h-3 w-3" />
            <span className="text-[8px] font-bold text-ink">your.business</span>
            <span className="ml-auto text-[7px] text-neutral-400">Sponsored</span>
        </div>
        <div className="relative flex-1 bg-gradient-to-br from-[#7A60E2] via-[#9b5de5] to-[#d62976]">
            <span className="absolute inset-0 m-auto flex h-6 w-6 items-center justify-center rounded-full bg-white/90">
                <svg viewBox="0 0 24 24" className="ml-0.5 h-3 w-3 fill-ink"><path d="M8 5v14l11-7z" /></svg>
            </span>
            <span className="absolute bottom-1 left-1.5 rounded bg-black/40 px-1 py-px text-[7px] font-bold text-white">Reel · 0:12</span>
        </div>
        <div className="flex items-center justify-between px-1.5 py-1">
            <span className="text-[7px] font-bold text-[#0095f6]">Book now</span>
            <span className="text-[7px] text-neutral-400">♡ 2.4k · 💬 118</span>
        </div>
    </div>
);

const CarouselAd = () => (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white text-left shadow-md">
        <div className="flex flex-1">
            <div className="flex-[2] bg-gradient-to-br from-[#7A60E2] to-[#4f5bd5]" />
            <div className="flex-1 border-l-2 border-white bg-gradient-to-br from-[#C4B5FD] to-[#7A60E2]" />
            <div className="flex-1 border-l-2 border-white bg-gradient-to-br from-[#EDE9FE] to-[#C4B5FD]" />
        </div>
        <div className="px-1.5 py-1">
            <p className="text-[8px] font-bold text-ink">Festive styling packages</p>
            <div className="mt-0.5 flex items-center justify-between">
                <span className="text-[7px] font-bold text-[#0095f6]">Swipe to explore →</span>
                <span className="text-[6px] tracking-wider text-neutral-400">● ○ ○</span>
            </div>
        </div>
    </div>
);

const RemarketingAd = () => (
    <div className="flex h-full w-full items-center gap-2 rounded-lg border border-neutral-800 bg-ink p-2.5 text-left shadow-md">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/10 text-sm font-bold text-white">%</span>
        <div className="min-w-0">
            <p className="truncate text-[9px] font-bold text-white">Still thinking it over?</p>
            <p className="text-[8px] leading-snug text-white/60">Your 20% welcome offer expires soon</p>
            <span className="mt-1 inline-block rounded-full bg-white px-2 py-0.5 text-[8px] font-bold text-ink">Claim offer</span>
        </div>
        <span className="ml-auto shrink-0 self-start text-[6px] font-bold uppercase tracking-wider text-white/40">Google · Display</span>
    </div>
);

const AD_CARDS = [SponsoredLinkAd, InstagramVideoAd, CarouselAd, RemarketingAd];
const AD_POSES = [
    { x: 0, y: -8, rotate: 0, scale: 1, opacity: 1, zIndex: 4 },
    { x: 12, y: 4, rotate: 5, scale: 0.94, opacity: 1, zIndex: 3 },
    { x: -12, y: 14, rotate: -6, scale: 0.88, opacity: 0.95, zIndex: 2 },
    { x: 6, y: 22, rotate: 3, scale: 0.82, opacity: 0.6, zIndex: 1 },
];

const AdsSearchMock = () => {
    const [order, setOrder] = useState([0, 1, 2, 3]);
    useEffect(() => {
        const t = setInterval(() => setOrder(([f, ...rest]) => [...rest, f]), 2400);
        return () => clearInterval(t);
    }, []);
    return (
        <div data-testid="ads-stack" className="flex h-full min-h-[150px] items-center justify-center overflow-hidden p-3">
            <div className="relative h-24 w-40">
                {order.map((cardIdx, pos) => {
                    const Ad = AD_CARDS[cardIdx];
                    return (
                        <motion.div
                            key={cardIdx}
                            className="absolute inset-0"
                            initial={false}
                            animate={AD_POSES[pos]}
                            transition={{ type: "spring", stiffness: 260, damping: 24 }}
                        >
                            <Ad />
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};

export const Compare = () => (
    <Section id="how-it-works" className="bg-[#7A60E2]">
        <div className="grid gap-4 lg:grid-cols-6 lg:grid-flow-dense">
            <Reveal className="lg:col-span-4">
                <div data-testid="bento-intro" className="flex h-full flex-col justify-center rounded-[2rem] p-4 sm:p-6">
                    <h2 className="font-display text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-white sm:text-4xl">
                        Become a Purple Business.
                    </h2>
                    <p className="mt-5 max-w-xl text-sm text-white/75 sm:text-base">
                        Right now, so much depends on you remembering, finding the time, and figuring it out. With
                        Purple, your reviews, ideas, campaigns and results become part of an ongoing routine — with AI
                        doing more of the repetitive work and you deciding what comes next.
                    </p>
                </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-2 lg:row-span-2">
                <div data-testid="bento-app-video" className="group flex h-full flex-col rounded-[2rem] bg-white p-6 transition-transform duration-300 hover:-translate-y-1">
                    <p className="font-display text-base font-bold tracking-[-0.02em] text-ink">One app for all of it.</p>
                    <div className="mt-5 flex-1 overflow-hidden rounded-2xl bg-white">
                        <LazyVideo src="/assets/video-app.mp4" className="h-full min-h-[200px] w-full object-cover [filter:brightness(1.25)]" />
                    </div>
                </div>
            </Reveal>

            <Card title="Purple Business focuses on every part of the customer journey" className="lg:col-span-2" delay={0.12} testId="bento-journey">
                <JourneyMock />
            </Card>
            <Card title="Reply with reviews with AI and rank higher in Google Search" className="lg:col-span-2" delay={0.16} testId="bento-reviews">
                <ReviewReplyMock />
            </Card>
            <Card title="Run high quality Ads on Google and Meta platforms" className="lg:col-span-2 lg:col-start-3" delay={0.1} testId="bento-ads">
                <AdsSearchMock />
            </Card>
        </div>
    </Section>
);
